import React from 'react'

import styled, { css } from 'styled-components'

import { ADVANCED_ACCOUNTING_CLASS_NAME } from '@constants/common'

import { AutoResizeTextarea } from './AutoResizeTextarea'
import { getInputBorderColor } from './helpers'
import type { TextareaProps } from './types'

const InputWrapper = styled.div<{
  $dense?: boolean
  $error?: boolean
  $hasAdornment?: boolean
  $disabled?: boolean
  $highlighted?: boolean
}>`
  --input-advanced-highlighted-border-color: ${({ $disabled, $error, $highlighted, theme }) =>
    getInputBorderColor({
      $disabled,
      $error,
      theme,
      $highlighted,
      $isAdvancedAccounting: true,
    })};
  --input-border-color: ${({ $disabled, $error, $highlighted, theme }) =>
    getInputBorderColor({ $disabled, $error, theme, $highlighted })};

  --input-vertical-padding: ${({ $dense }) => ($dense ? '4px' : '6px')};
  --input-horizontal-padding: ${({ $dense }) => ($dense ? '8px' : '10px')};
  --input-height: ${({ $dense }) => ($dense ? 26 : 36)}px;
  --input-focus-border-color: ${({ theme }) => theme.colors.common.outlineBlue};

  --input-disabled-bg-color: ${({ theme }) => theme.colors.field.disabledBackground};
  --input-bg-color: ${({ theme }) => theme.colors.field.background};

  display: flex;
  align-items: center;
  width: 100%;
  height: auto;
  font-size: 1rem;
  line-height: 1.3;

  ${({ $hasAdornment, $disabled }) =>
    $hasAdornment
      ? css`
          gap: 4px;
          padding: 0 var(--input-horizontal-padding);
          border-width: 1px;
          border-style: solid;
          border-color: var(--input-border-color);
          border-radius: 4px;
          background-color: ${$disabled ? 'var(--input-disabled-bg-color)' : 'var(--input-bg-color)'};

          :focus-within {
            border-color: var(--input-focus-border-color);
          }

          input {
            border: none;
            background-color: transparent;
            padding: 0;
            height: calc(var(--input-height) - 2px);
          }
        `
      : css`
          padding: 0;

          input {
            border-width: 1px;
            border-style: solid;
            border-color: var(--input-border-color);
            border-radius: 4px;
            background-color: var(--input-bg-color);
            padding: var(--input-vertical-padding) var(--input-horizontal-padding);
            height: var(--input-height);

            :focus {
              border-color: var(--input-focus-border-color);
            }
            :disabled {
              background-color: var(--input-disabled-bg-color);
            }
          }
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: var(--input-disabled-bg-color);
    `}

  ${({ $highlighted, $hasAdornment }) =>
    $highlighted &&
    ($hasAdornment
      ? css`
          .${ADVANCED_ACCOUNTING_CLASS_NAME} & {
            border-color: var(--input-advanced-highlighted-border-color);

            :focus-within {
              border-color: var(--input-focus-border-color);
            }
          }
        `
      : css`
          .${ADVANCED_ACCOUNTING_CLASS_NAME} & input {
            border-color: var(--input-advanced-highlighted-border-color);

            :focus {
              border-color: var(--input-focus-border-color);
            }
          }
        `)};
`

const StyledTextarea = styled(AutoResizeTextarea)<{
  $dense?: boolean
  $error?: boolean
  $hasAdornment?: boolean
}>`
  font: inherit;
  color: inherit;
  flex: 1 1 auto;
  min-width: 0;
  text-align: inherit;
  color: ${({ theme }) => theme.colors.field.value};
  resize: none;
  min-height: var(--input-height);

  ${({ maxRows }) =>
    maxRows &&
    css`
      max-height: calc(${Math.ceil(maxRows * 1.3)}rem + 2 * var(--input-vertical-padding) + 2px);
    `}

  :focus {
    outline: none;
  }
  :disabled {
    color: ${({ theme }) => theme.colors.field.disabledValue};
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.field.placeholder};
  }

  :disabled::placeholder {
    color: ${({ theme }) => theme.colors.field.disabledPlaceholder};
  }

  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    background-color 0.2s;
  padding: var(--input-vertical-padding) var(--input-horizontal-padding);
  border-width: 1px;
  border-style: solid;
  border-color: var(--input-border-color);
  border-radius: 4px;
  background-color: var(--input-bg-color);

  :focus {
    border-color: var(--input-focus-border-color);
  }
  :disabled {
    background-color: var(--input-disabled-bg-color);
  }
`

const Adornment = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    className,
    dense = false,
    error = false,
    highlighted = false,
    id,
    startAdornment,
    endAdornment,
    minRows = 1,
    maxRows,
    ...rest
  },
  ref
) {
  const inputId = id
  const hasAdornment = Boolean(startAdornment || endAdornment)

  return (
    <InputWrapper
      className={className}
      $dense={dense}
      $disabled={rest.disabled}
      $error={error}
      $hasAdornment={hasAdornment}
      $highlighted={highlighted}
    >
      {startAdornment && <Adornment>{startAdornment}</Adornment>}
      <StyledTextarea
        ref={ref}
        id={inputId}
        $dense={dense}
        $error={error}
        $hasAdornment={hasAdornment}
        minRows={minRows}
        maxRows={maxRows}
        {...rest}
      />
      {endAdornment && <Adornment>{endAdornment}</Adornment>}
    </InputWrapper>
  )
})

Textarea.displayName = 'Textarea'
